import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { TesSalaryTablePublishCandidateSelectionCountByType } from "../../tesSalaryTables/tesSalaryTablesApi";

type PublishSourceSelection = {
  EMPLOYMENT_ATTRIBUTES: boolean;
  SHIFT_EMPLOYMENT: boolean;
  EMPLOYMENT: boolean;
};

type ProjectionCatchUpStatus = {
  completed: number;
  target: number;
  ready: boolean;
};

const isPublishSourceEnabled = (
  publishEstimatedByType: TesSalaryTablePublishCandidateSelectionCountByType | null,
  source: keyof PublishSourceSelection,
) => {
  if (!publishEstimatedByType) return true;
  if (source === "EMPLOYMENT_ATTRIBUTES") {
    return publishEstimatedByType.employmentAttributes.matching > 0;
  }
  if (source === "SHIFT_EMPLOYMENT") {
    return publishEstimatedByType.shiftEmployments.matching > 0;
  }
  return publishEstimatedByType.employments.matching > 0;
};

type Props = {
  open: boolean;
  isBusy: boolean;
  targetTableLabel: string | null;
  sourceTableLabel: string | null;
  publishesSalaryChanges: boolean;
  canPublishWithoutSalaryChanges: boolean;
  publishEstimatedTotal: number | null;
  publishEstimatedByType: TesSalaryTablePublishCandidateSelectionCountByType | null;
  publishProcessedTotal: number;
  publishSkippedTotal: number;
  publishProgressPercent: number | null;
  publishPausedReason: string | null;
  publishWaitingSeconds: number | null;
  publishProjectionCatchUp: ProjectionCatchUpStatus | null;
  isPublishing: boolean;
  canContinue: boolean;
  publishSourceSelection: PublishSourceSelection;
  onPublishSourceSelectionChange: (
    next:
      | PublishSourceSelection
      | ((current: PublishSourceSelection) => PublishSourceSelection),
  ) => void;
  onClose: () => void;
  onConfirm: () => void;
  onPause: () => void;
  onContinueNow: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const TesSalaryTablePublishDialog = ({
  open,
  isBusy,
  targetTableLabel,
  sourceTableLabel,
  publishesSalaryChanges,
  canPublishWithoutSalaryChanges,
  publishEstimatedTotal,
  publishEstimatedByType,
  publishProcessedTotal,
  publishSkippedTotal,
  publishProgressPercent,
  publishPausedReason,
  publishWaitingSeconds,
  publishProjectionCatchUp,
  isPublishing,
  canContinue,
  publishSourceSelection,
  onPublishSourceSelectionChange,
  onClose,
  onConfirm,
  onPause,
  onContinueNow,
  t,
}: Props) => {
  const employmentAttributesEnabled = isPublishSourceEnabled(
    publishEstimatedByType,
    "EMPLOYMENT_ATTRIBUTES",
  );
  const shiftEmploymentsEnabled = isPublishSourceEnabled(
    publishEstimatedByType,
    "SHIFT_EMPLOYMENT",
  );
  const employmentsEnabled = isPublishSourceEnabled(
    publishEstimatedByType,
    "EMPLOYMENT",
  );
  const enabledSourceCount = [
    employmentAttributesEnabled,
    shiftEmploymentsEnabled,
    employmentsEnabled,
  ].filter(Boolean).length;
  const allPublishSourcesSelected =
    (!employmentAttributesEnabled ||
      publishSourceSelection.EMPLOYMENT_ATTRIBUTES) &&
    (!shiftEmploymentsEnabled || publishSourceSelection.SHIFT_EMPLOYMENT) &&
    (!employmentsEnabled || publishSourceSelection.EMPLOYMENT);
  const hasSelectedPublishSource =
    publishSourceSelection.EMPLOYMENT_ATTRIBUTES ||
    publishSourceSelection.SHIFT_EMPLOYMENT ||
    publishSourceSelection.EMPLOYMENT;
  const publishSourceSelectionDisabled = isBusy || canContinue;

  return (
    <DialogComponent autoWidth open={open} onClose={onClose}>
      <Box sx={{ p: 3, minWidth: 420, position: "relative" }}>
        <OverlaySpinner isLoading={isBusy && !isPublishing} />
        <Typography variant="h6" fontWeight={700} marginBottom={1}>
          {t("settings:tesSalaryTablesPublish")}
        </Typography>
        <Typography marginBottom={3}>
          {t("settings:tesSalaryTablesPublishConfirm")}
        </Typography>
        {targetTableLabel && (
          <Typography color="text.primary">
            {t("settings:tesSalaryTablesPublishTargetTable")}:{" "}
            {targetTableLabel}
          </Typography>
        )}
        {sourceTableLabel && (
          <Typography color="text.primary" marginBottom={3}>
            {t("settings:tesSalaryTablesPublishSourceTable")}:{" "}
            {sourceTableLabel}
          </Typography>
        )}
        {publishesSalaryChanges && publishEstimatedTotal !== null && (
          <Typography color="text.primary" marginBottom={1}>
            {t("settings:tesSalaryTablesPublishEstimatedCount", {
              count: publishEstimatedTotal,
            })}
          </Typography>
        )}
        {publishesSalaryChanges && publishEstimatedByType && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allPublishSourcesSelected}
                  indeterminate={
                    !allPublishSourcesSelected && hasSelectedPublishSource
                  }
                  disabled={
                    enabledSourceCount === 0 || publishSourceSelectionDisabled
                  }
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onPublishSourceSelectionChange({
                      EMPLOYMENT_ATTRIBUTES: employmentAttributesEnabled
                        ? checked
                        : false,
                      SHIFT_EMPLOYMENT: shiftEmploymentsEnabled
                        ? checked
                        : false,
                      EMPLOYMENT: employmentsEnabled ? checked : false,
                    });
                  }}
                />
              }
              label="Select all sources"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={publishSourceSelection.EMPLOYMENT_ATTRIBUTES}
                  disabled={
                    !employmentAttributesEnabled ||
                    publishSourceSelectionDisabled
                  }
                  onChange={(event) =>
                    onPublishSourceSelectionChange((current) => ({
                      ...current,
                      EMPLOYMENT_ATTRIBUTES: event.target.checked,
                    }))
                  }
                />
              }
              label={
                <Typography color="text.primary" variant="body2">
                  Future shift salaries: matching{" "}
                  {publishEstimatedByType.employmentAttributes.matching}, no
                  table:{" "}
                  {
                    publishEstimatedByType.employmentAttributes
                      .nullTesSalaryTableId
                  }
                  , target table:{" "}
                  {
                    publishEstimatedByType.employmentAttributes
                      .alreadyTargetTesSalaryTableId
                  }
                  , other table:{" "}
                  {
                    publishEstimatedByType.employmentAttributes
                      .mismatchingTesSalaryTableId
                  }
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={publishSourceSelection.SHIFT_EMPLOYMENT}
                  disabled={
                    !shiftEmploymentsEnabled || publishSourceSelectionDisabled
                  }
                  onChange={(event) =>
                    onPublishSourceSelectionChange((current) => ({
                      ...current,
                      SHIFT_EMPLOYMENT: event.target.checked,
                    }))
                  }
                />
              }
              label={
                <Typography color="text.primary" variant="body2">
                  Shift employments: matching{" "}
                  {publishEstimatedByType.shiftEmployments.matching}, no table:{" "}
                  {publishEstimatedByType.shiftEmployments.nullTesSalaryTableId}
                  , target table:{" "}
                  {
                    publishEstimatedByType.shiftEmployments
                      .alreadyTargetTesSalaryTableId
                  }
                  , other table:{" "}
                  {
                    publishEstimatedByType.shiftEmployments
                      .mismatchingTesSalaryTableId
                  }
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={publishSourceSelection.EMPLOYMENT}
                  disabled={
                    !employmentsEnabled || publishSourceSelectionDisabled
                  }
                  onChange={(event) =>
                    onPublishSourceSelectionChange((current) => ({
                      ...current,
                      EMPLOYMENT: event.target.checked,
                    }))
                  }
                />
              }
              label={
                <Typography color="text.primary" variant="body2">
                  Employments: matching{" "}
                  {publishEstimatedByType.employments.matching}, no table:{" "}
                  {publishEstimatedByType.employments.nullTesSalaryTableId},
                  target table:{" "}
                  {
                    publishEstimatedByType.employments
                      .alreadyTargetTesSalaryTableId
                  }
                  , other table:{" "}
                  {
                    publishEstimatedByType.employments
                      .mismatchingTesSalaryTableId
                  }
                </Typography>
              }
            />
          </Box>
        )}
        {publishesSalaryChanges && (
          <Typography color="text.primary" marginBottom={2}>
            {enabledSourceCount === 0
              ? t("settings:tesSalaryTablesPublishNothingToPublish")
              : t("settings:tesSalaryTablesPublishProcessedCount", {
                  count: publishProcessedTotal,
                })}
            {enabledSourceCount > 0 && publishProgressPercent !== null
              ? ` (${publishProgressPercent}%)`
              : ""}
          </Typography>
        )}
        {publishesSalaryChanges && enabledSourceCount > 0 && (
          <Typography color="text.primary" marginBottom={2}>
            {t("settings:tesSalaryTablesPublishSkippedCount", {
              count: publishSkippedTotal,
            })}
          </Typography>
        )}
        {publishPausedReason && (
          <Typography color="warning.main" marginBottom={2}>
            {publishPausedReason}
          </Typography>
        )}
        {publishWaitingSeconds !== null && (
          <Typography color="text.secondary" marginBottom={2}>
            {t("settings:tesSalaryTablesPublishWaitingNextPass", {
              seconds: publishWaitingSeconds,
            })}
            {publishProjectionCatchUp
              ? ` ${t("settings:tesSalaryTablesPublishProjectionCatchUp", {
                  completed: publishProjectionCatchUp.completed,
                  target: publishProjectionCatchUp.target,
                })}`
              : ""}
          </Typography>
        )}
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" disabled={isBusy} onClick={onClose}>
            {t("common:cancel")}
          </Button>
          {publishWaitingSeconds !== null && (
            <Button variant="outlined" onClick={onContinueNow}>
              {t("settings:tesSalaryTablesPublishContinueNow")}
            </Button>
          )}
          {isPublishing && (
            <Button variant="outlined" onClick={onPause}>
              {t("settings:tesSalaryTablesPublishPause")}
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={
              isBusy ||
              (publishesSalaryChanges &&
                !hasSelectedPublishSource &&
                !canPublishWithoutSalaryChanges)
            }
            onClick={onConfirm}
          >
            {canContinue
              ? t("common:continue")
              : t("settings:tesSalaryTablesPublish")}
          </Button>
        </Box>
      </Box>
    </DialogComponent>
  );
};

export default TesSalaryTablePublishDialog;
